import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckboxGroup, FieldShell, ProgressBar, RadioGroup, TextField } from '@/components/FormControls';
import SuccessState from '@/components/SuccessState';
import { buildSubmission, emptyForm, submitLead } from '@/api/leads';
import { track } from '@/lib/analytics';
import { captureUtm } from '@/lib/utm';
import { hasErrors, validateStep } from '@/lib/validation';
import type { FieldErrors, LeadFormState } from '@/types/lead';
import { errors as E, form } from '@/config/collectionsLanding';

type Status = 'editing' | 'submitting' | 'success';

export default function LeadForm() {
  const [state, setState] = useState<LeadFormState>(emptyForm);
  const [step, setStep] = useState(0);
  const [errs, setErrs] = useState<FieldErrors>({});
  const [status, setStatus] = useState<Status>('editing');
  const [formError, setFormError] = useState<string>('');
  const startedRef = useRef(false);
  const priceSeenRef = useRef(false);
  const utm = useMemo(() => captureUtm(), []);

  useEffect(() => {
    track('collections_step_view', { step });
  }, [step]);

  const set = <K extends keyof LeadFormState>(key: K, val: LeadFormState[K]) => {
    if (!startedRef.current) {
      startedRef.current = true;
      track('collections_form_started');
    }
    setState((s) => ({ ...s, [key]: val }));
    setErrs((e) => ({ ...e, [key]: undefined }));
  };

  const toggle = (key: 'payment_methods' | 'collection_pains', val: string) => {
    const current = state[key];
    const next = current.includes(val)
      ? current.filter((v) => v !== val)
      : [...current, val];
    set(key, next);
    if (key === 'payment_methods') track('collections_payment_method_selected', { value: val });
    if (key === 'collection_pains') track('collections_pain_selected', { value: val });
  };

  const goNext = () => {
    const stepErrs = validateStep(step, state);
    if (hasErrors(stepErrs)) {
      setErrs(stepErrs);
      track('collections_form_error', { step, fields: Object.keys(stepErrs) });
      return;
    }
    setErrs({});
    const next = step + 1;
    setStep(next);
    if (next === 1 && !priceSeenRef.current) {
      priceSeenRef.current = true;
      track('collections_price_seen');
    }
  };

  const goBack = () => {
    setErrs({});
    setStep((s) => Math.max(0, s - 1));
  };

  const onSubmit = async () => {
    const stepErrs = validateStep(1, state);
    if (hasErrors(stepErrs)) {
      setErrs(stepErrs);
      track('collections_form_error', { step: 1, fields: Object.keys(stepErrs) });
      return;
    }
    // Honeypot: silently accept but do not submit real spam.
    if (state.company_website.trim()) {
      setStatus('success');
      return;
    }
    setStatus('submitting');
    setFormError('');
    try {
      const res = await submitLead(buildSubmission(state, utm));
      if (res.ok) {
        track('collections_lead_submitted', {
          invoicing_platform: state.invoicing_platform,
          price_commitment: state.price_commitment,
          duplicate: Boolean(res.duplicate),
        });
        setStatus('success');
      } else {
        track('collections_form_error', { step: 'submit', error: res.error });
        setFormError(res.error === 'validation' ? E.generic : E.network);
        setStatus('editing');
      }
    } catch {
      track('collections_form_error', { step: 'submit', error: 'network' });
      setFormError(E.network);
      setStatus('editing');
    }
  };

  if (status === 'success') {
    return <SuccessState invoicingPlatform={state.invoicing_platform} />;
  }

  const platformOpt = form.invoicingPlatform.options.find(
    (o) => o.value === state.invoicing_platform,
  );
  const showPlatformOther = Boolean(platformOpt?.freeText);
  const showPaymentOther = state.payment_methods.includes('other');

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50 sm:p-8">
      <ProgressBar labels={form.steps.labels} current={step} />

      {/* Honeypot - visually hidden, not announced. */}
      <div className="absolute h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor="company_website">Company website</label>
        <input
          id="company_website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={state.company_website}
          onChange={(e) => setState((s) => ({ ...s, company_website: e.target.value }))}
        />
      </div>

      {step === 0 && (
        <div className="space-y-6">
          <FieldShell
            label={form.invoicingPlatform.question}
            htmlFor="invoicing_platform"
            error={errs.invoicing_platform}
          >
            <RadioGroup
              name="invoicing_platform"
              options={form.invoicingPlatform.options}
              value={state.invoicing_platform}
              onChange={(v) => {
                set('invoicing_platform', v);
                track('collections_invoicing_platform_selected', { value: v });
              }}
            />
          </FieldShell>
          {showPlatformOther && (
            <TextField
              id="invoicing_platform_other"
              label={form.invoicingPlatform.otherPlaceholder}
              value={state.invoicing_platform_other}
              onChange={(v) => set('invoicing_platform_other', v)}
              error={errs.invoicing_platform_other}
            />
          )}

          <FieldShell
            label={form.paymentMethods.question}
            error={errs.payment_methods}
          >
            <CheckboxGroup
              options={form.paymentMethods.options}
              values={state.payment_methods}
              onToggle={(v) => toggle('payment_methods', v)}
            />
          </FieldShell>
          {showPaymentOther && (
            <TextField
              id="payment_method_other"
              label={form.paymentMethods.otherPlaceholder}
              value={state.payment_method_other}
              onChange={(v) => set('payment_method_other', v)}
              optional
            />
          )}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-brand-100 bg-brand-50 p-5 text-center">
            <p className="text-sm font-semibold text-brand-700">גישה מוקדמת</p>
            <h3 className="mt-2 text-2xl font-extrabold text-ink-900">
              {form.priceCommitment.question}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-600">
              השאירו אימייל ונייד, ונחזור אליכם לפתיחת הגישה.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="first_name"
              label={form.contact.firstName.label}
              placeholder={form.contact.firstName.placeholder}
              autoComplete="given-name"
              value={state.first_name}
              onChange={(v) => set('first_name', v)}
              error={errs.first_name}
              optional
            />
            <TextField
              id="business_name"
              label={form.contact.businessName.label}
              placeholder={form.contact.businessName.placeholder}
              autoComplete="organization"
              value={state.business_name}
              onChange={(v) => set('business_name', v)}
              optional
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="email"
              type="email"
              inputMode="email"
              label={form.contact.email.label}
              placeholder={form.contact.email.placeholder}
              autoComplete="email"
              value={state.email}
              onChange={(v) => set('email', v)}
              error={errs.email}
            />
            <TextField
              id="mobile"
              type="tel"
              inputMode="tel"
              label={form.contact.mobile.label}
              placeholder={form.contact.mobile.placeholder}
              autoComplete="tel"
              value={state.mobile}
              onChange={(v) => set('mobile', v)}
              error={errs.mobile}
            />
          </div>
          <p className="-mt-3 text-sm text-ink-500">{form.contact.contactHelper}</p>

          <TextField
            id="business_type"
            label={form.contact.businessType.label}
            placeholder={form.contact.businessType.placeholder}
            value={state.business_type}
            onChange={(v) => set('business_type', v)}
            optional
          />

          <p className="-mt-2 text-xs leading-relaxed text-ink-500">{form.contact.consent}</p>

          {formError && (
            <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {formError}
            </p>
          )}
        </div>
      )}

      <div className="mt-8 flex items-center justify-between gap-3">
        {step > 0 ? (
          <button
            type="button"
            onClick={goBack}
            className="rounded-xl px-5 py-3 text-base font-semibold text-ink-600 transition hover:bg-slate-100"
          >
            חזרה
          </button>
        ) : (
          <span />
        )}

        {step < 1 ? (
          <button
            type="button"
            onClick={goNext}
            className="rounded-xl bg-brand-600 px-7 py-3 text-base font-semibold text-white shadow-lg shadow-brand-600/20 transition hover:bg-brand-700 active:scale-[0.99]"
          >
            המשך
          </button>
        ) : (
          <button
            type="button"
            onClick={onSubmit}
            disabled={status === 'submitting'}
            className="rounded-xl bg-brand-600 px-7 py-3 text-base font-semibold text-white shadow-lg shadow-brand-600/20 transition hover:bg-brand-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {status === 'submitting' ? form.contact.submitting : form.contact.submit}
          </button>
        )}
      </div>
    </div>
  );
}
